import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { drawerWidth } from '../style';
import { useDispatch, useSelector } from 'react-redux';
import { hideMenu } from '../../../../store/actions/CommonAction';
import { ClickAwayListener, Typography, Box, Fade } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	MENU_ARTIST,
	MENU_FANCLUB,
	MENU_MOBILE,
	MENU_MUSIC,
	MENU_PLANNER,
	MENU_STORE,
	MENU_SUPPORT,
	MENU_WEBSITE
} from '../../../../store/reducers/CommonReducer';
import MenuWebsite from './MenuWebsite';
import MenuArtist from './MenuArtist';
import MenuPlanner from './MenuPlanner';
import MenuMusic from './MenuMusic';
import MenuFanclub from './MenuFanclub';
import MenuStore from './MenuStore';
import MenuSupport from './MenuSupport';
import useMediaQuery from '@mui/material/useMediaQuery';

const MenuContainer = styled(Box, {
	shouldForwardProp: prop => prop !== 'scrollTop'
})(({ theme, scrollTop = 0 }) => ({
	height: '100vh',
	position: 'fixed',
	top: 0,
	left: drawerWidth,
	zIndex: theme.zIndex.drawer,
	paddingTop: Math.max(64 - scrollTop, 0) + 16,
	background: 'linear-gradient(180deg, rgba(30, 30, 30, 0.98) 0%, rgba(18, 18, 18, 0.98) 100%)',
	backdropFilter: 'blur(10px)',
	width: '240px',
	maxWidth: '240px',
	overflowY: 'auto',
	overflowX: 'hidden',
	borderRight: '1px solid rgba(255, 255, 255, 0.08)',
	boxShadow: '4px 0 24px rgba(0, 0, 0, 0.3)',
	display: 'flex',
	flexDirection: 'column',
}));

const MenuHeader = styled(Box)(({ theme }) => ({
	padding: theme.spacing(2, 2, 1, 2),
	borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
	marginBottom: theme.spacing(1),
}));

const MenuTitle = styled(Typography)(({ theme }) => ({
	fontSize: '0.75rem',
	fontWeight: 600,
	textTransform: 'uppercase',
	letterSpacing: '0.5px',
	color: 'rgba(255, 255, 255, 0.5)',
}));

const MenuContent = styled(Box)(({ theme }) => ({
	flex: 1,
	padding: theme.spacing(0, 1),
	'& .MuiList-root': {
		padding: 0,
	},
	'& .MuiListItem-root, & .MuiListItemButton-root': {
		borderRadius: theme.spacing(1),
		marginBottom: theme.spacing(0.5),
		padding: theme.spacing(1, 1.5),
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
		},
	},
	'& .MuiListItemIcon-root': {
		minWidth: 36,
		color: 'rgba(255, 255, 255, 0.7)',
	},
	'& .MuiListItemText-primary': {
		fontSize: '0.875rem',
	},
}));

const Menu = () => {
	const dispatch = useDispatch();
	const menu = useSelector(state => state.common.menu);
	const theme = useTheme();
	const mobile = useMediaQuery(theme.breakpoints.down('md'));
	const [scrollY, setScrollY] = useState(0);
	const { t } = useTranslation();

	useEffect(() => {
		const handleScroll = () => setScrollY(window.pageYOffset);
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const handleHideMenu = () => dispatch(hideMenu());

	if (!menu || menu === MENU_MOBILE || mobile) return null;

	const getMenuContent = () => {
		switch (menu) {
			case MENU_WEBSITE:
				return <MenuWebsite />;
			case MENU_ARTIST:
				return <MenuArtist />;
			case MENU_MUSIC:
				return <MenuMusic />;
			case MENU_PLANNER:
				return <MenuPlanner />;
			case MENU_FANCLUB:
				return <MenuFanclub />;
			case MENU_STORE:
				return <MenuStore />;
			case MENU_SUPPORT:
				return <MenuSupport />;
			default:
				return null;
		}
	};

	return (
		<ClickAwayListener onClickAway={handleHideMenu}>
			<Fade in={true} timeout={200}>
				<MenuContainer scrollTop={scrollY}>
					<MenuHeader>
						<MenuTitle>
							{t('menu_choose_feature')}
						</MenuTitle>
					</MenuHeader>
					<MenuContent>
						{getMenuContent()}
					</MenuContent>
				</MenuContainer>
			</Fade>
		</ClickAwayListener>
	);
};

export default Menu;